import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, XCircleIcon, InfoIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SiteFooter } from '../components/SiteFooter';
import { GRITVUE_BATCH_AUTH_CODE, normalizeCode } from '../data/authenticity';
import { RX_INSERT_UPDATES_URL, RX_INSERT_FALLBACK_MAILTO } from '../data/rxInsert';
import { trackCustom } from '../utils/tracking';
type Status = 'idle' | 'verified' | 'mismatch' | 'unavailable';
const nextSteps = [{
  title: 'Need a backup X1?',
  body: 'Keep a spare charged in the truck.',
  to: '/#offer'
}, {
  title: 'Equip your crew',
  body: 'Business / crew pack, one order.',
  to: '/#company'
}, {
  title: 'Refer a friend & earn',
  body: 'GritVue Crew Rewards.',
  to: '/rewards'
}];
export function Authenticity() {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  useEffect(() => {
    window.scrollTo(0, 0);
    trackCustom('authenticity_page_visited');
  }, []);
  const verify = (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim()) return;
    trackCustom('batch_verification_attempted');
    if (!GRITVUE_BATCH_AUTH_CODE) {
      setStatus('unavailable');
      return;
    }
    if (normalizeCode(code) === normalizeCode(GRITVUE_BATCH_AUTH_CODE)) {
      setStatus('verified');
      trackCustom('batch_verification_successful');
    } else {
      setStatus('mismatch');
    }
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1193" data-mp="3kluxt17vm000">
      <PageHeader data-id="element-1194" data-mp="38it594ltu2u" />
      <main className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:grid lg:grid-cols-[1fr_1fr] lg:gap-16 lg:px-8 lg:py-20" data-id="element-1195" data-mp="390yqwd5pknf">
        <div data-id="element-1196" data-mp="36w17eegq37o6">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1197" data-mp="36vj3qwrqdr68">Batch authenticity check</p>
          <h1 className="grit-headline mt-3 text-[clamp(2.5rem,8vw,4.75rem)] font-extrabold uppercase leading-[0.92]" data-id="element-1198" data-mp="31wewiboda3ij">
            Verify your
            <br data-id="element-1199" data-mp="15s8sgn4lesoo" />
            <span className="text-grit-500" data-id="element-1200" data-mp="1aswl1d8gzhl0">GritVue.</span>
          </h1>
          <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-steel-300 sm:text-lg" data-id="element-1201" data-mp="1aqtk3y5s441i">
            Enter the code printed on the authenticity card in your box. We&apos;ll confirm it matches an authorized
            GritVue X1 production batch.
          </p>
          <p className="mt-4 flex max-w-lg items-start gap-2 text-[13px] leading-relaxed text-steel-500" data-id="element-1202" data-mp="mwiowvh0ho8o">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1203" data-mp="rutzj8f4s2ry" />
            This checks the production batch. A batch code is shared by the units in that batch, so it does not identify
            one individual unit.
          </p>
        </div>

        <div className="mt-10 lg:mt-0" data-id="element-1204" data-mp="27bkepwnvwdl8">
          <form onSubmit={verify} className="border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-1205" data-mp="27aj57wminz5m">
            <label htmlFor="batch-code" className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1206" data-mp="2c8kjm48zzaar">
              Authenticity card code
            </label>
            <input id="batch-code" value={code} onChange={event => {
            setCode(event.target.value);
            if (status !== 'idle') setStatus('idle');
          }} autoComplete="off" autoCapitalize="characters" placeholder="Enter code" className="mt-2 w-full border-2 border-white/20 bg-ink-950 px-4 py-3.5 font-display text-2xl font-bold uppercase tracking-[0.12em] text-offwhite placeholder:text-steel-500 focus:border-grit-500 focus:outline-none" data-id="element-1207" data-mp="2heppmmw6mgwc" />
            <button type="submit" disabled={!code.trim()} className="mt-4 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1208" data-mp="1pobg12v7bqqh">
              Check my code
            </button>

            <AnimatePresence mode="wait" data-id="element-1209" data-mp="3i3b2lq2y8gjq">
              {status !== 'idle' && <motion.div key={status} role="status" initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease: [0.23, 1, 0.32, 1]
            }} className="mt-5" data-id="element-1210" data-mp="3m5epdav2j7kb">
                  {status === 'verified' && <div className="border-2 border-grit-500 bg-grit-500/10 p-5" data-id="element-1211" data-mp="33eoplv0so0vo">
                      <p className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase text-grit-500" data-id="element-1212" data-mp="2pfx9y46aa75f">
                        <BadgeCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-1213" data-mp="2udqgiybi1368" />
                        Verified GritVue production batch
                      </p>
                      <p className="mt-2 text-[15px] text-steel-200" data-id="element-1214" data-mp="25tvkh8pv66s8">
                        Your code matches an authorized GritVue X1 production batch. Welcome to GritVue.
                      </p>
                    </div>}
                  {status === 'mismatch' && <div className="border border-white/20 bg-white/[0.03] p-5" data-id="element-1215" data-mp="2trui60220jbx">
                      <p className="flex items-center gap-2 font-display text-xl font-bold uppercase text-offwhite" data-id="element-1216" data-mp="2tprfaffsyvps">
                        <XCircleIcon className="h-6 w-6 text-steel-400" aria-hidden="true" data-id="element-1217" data-mp="33hnr0tatakch" />
                        We couldn&apos;t match that code
                      </p>
                      <p className="mt-2 text-[14px] text-steel-300" data-id="element-1218" data-mp="2yknm4lpp7nmy">
                        Check it against your card and try again. Still no match? Email support@gritvuegear.com with a
                        photo of the card and where you bought it.
                      </p>
                    </div>}
                  {status === 'unavailable' && <div className="border border-white/20 bg-white/[0.03] p-5" data-id="element-1219" data-mp="2a5pbxd8kulmu">
                      <p className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-1220" data-mp="2e5gdmbzn6m65">Batch verification is being set up</p>
                      <p className="mt-2 text-[14px] text-steel-300" data-id="element-1221" data-mp="2iyiw9p2vsb7k">
                        Email your code and a photo of the card to support@gritvuegear.com and we&apos;ll confirm it for
                        you.
                      </p>
                    </div>}
                </motion.div>}
            </AnimatePresence>
          </form>

          {status === 'verified' && <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-1222" data-mp="31tr8myinprmi">
              {nextSteps.map(item => <li key={item.title} data-id="element-1223" data-mp="13itwtim39b2c">
                  <Link to={item.to} className="group flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1224" data-mp="17havddrg1d85">
                    <span data-id="element-1225" data-mp="q8qdo03vkdw9">
                      <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1226" data-mp="q7yszkbtg0kr">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1227" data-mp="1ix5oeafz4a1s">{item.body}</span>
                    </span>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1228" data-mp="1dy2sbfhz1e5y" />
                  </Link>
                </li>)}
              <li data-id="element-1229" data-mp="14xmuomnwt190">
                <a href={RX_INSERT_UPDATES_URL || RX_INSERT_FALLBACK_MAILTO} onClick={() => trackCustom('rx_insert_updates_clicked', {
              from: 'authenticity'
            })} className="group flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1230" data-mp="14y61p8c0u589">
                  <span data-id="element-1231" data-mp="1xmu1qxo0slds">
                    <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1232" data-mp="1xndocrom7xy5">
                      Get RX Insert updates
                    </span>
                    <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1233" data-mp="1okuli7ktxlbw">Prototype in development.</span>
                  </span>
                  <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1234" data-mp="2cbqh7fsep0of" />
                </a>
              </li>
            </ul>}
        </div>
      </main>
      <SiteFooter data-id="element-1235" data-mp="22km5qq1xvxyc" />
    </div>;
}